"use client";

/**
 * The one definition of "a product plus its vendor".
 *
 * The product-selection step pairs a 14px semibold product name with a 12px
 * muted vendor, which the Jul 2026 demo tester singled out as the thing that
 * made the vendor stick. Every other product reference renders through this
 * module so it reads the same way (ELI-1105).
 *
 * `IntakeOptionCard` keeps its own markup on purpose: its `secondaryLabel`
 * is generic — the problem step passes a category through it — so that card is
 * the *reference* for these tokens rather than a consumer of them.
 */

export interface ProductIdentity {
  name: string;
  vendor?: string;
}

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function identityTokens(value: string): string[] {
  return collapseWhitespace(value)
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function containsTokenSequence(name: string, vendor: string): boolean {
  const nameTokens = identityTokens(name);
  const vendorTokens = identityTokens(vendor);
  if (vendorTokens.length === 0) return false;

  return nameTokens.some((_, start) =>
    vendorTokens.every(
      (vendorToken, offset) => nameTokens[start + offset] === vendorToken,
    ),
  );
}

/**
 * The vendor worth showing, or `undefined`.
 *
 * A vendor the product name already carries ("Ava Health Scribe" by "Ava
 * Health") is dropped: it is on screen either way, and repeating it reads
 * worse than leaving it out.
 */
function displayVendor(identity: ProductIdentity): string | undefined {
  const vendor = collapseWhitespace(identity.vendor ?? "");
  if (!vendor) return undefined;
  return containsTokenSequence(identity.name, vendor) ? undefined : vendor;
}

/** Plain-text form, for chat echoes and accessible names. */
export function formatProductIdentity(identity: ProductIdentity): string {
  const name = collapseWhitespace(identity.name);
  const vendor = displayVendor(identity);
  return vendor ? `${name} by ${vendor}` : name;
}

/**
 * Vendor first, then product, on one line — the report's header badge, which
 * the design writes "Aiva Health · Ambient nursing". Takes the same
 * `displayVendor` rule as everything else here, so "Altair Prior
 * Authorization" by "Altair" renders once rather than twice.
 */
export function formatProductBadge(identity: ProductIdentity): string {
  const name = collapseWhitespace(identity.name);
  const vendor = displayVendor(identity);
  if (!name) return vendor ?? "";
  return vendor ? `${vendor} · ${name}` : name;
}

/** Inline name + vendor, matching the product card. */
export function ProductVendorLabel({
  identity,
  nameClassName = "font-semibold",
}: {
  identity: ProductIdentity;
  /** Defaults to the product-card weight; callers may opt out for plain text. */
  nameClassName?: string;
}) {
  const vendor = displayVendor(identity);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      <span className={`text-[14px] ${nameClassName} text-(--fg-primary)`}>
        {collapseWhitespace(identity.name)}
      </span>
      {vendor && (
        <span className="text-[12px] text-(--fg-muted)">{vendor}</span>
      )}
    </span>
  );
}

/**
 * The identity line for a downstream section header. Renders nothing when
 * there is no named product, so a summary without one — including the
 * projection-failure fallback — looks exactly as it did before.
 */
export function ProductIdentityLine({
  products,
  className,
}: {
  products: readonly ProductIdentity[];
  className?: string;
}) {
  const named = products.filter(
    (product) => collapseWhitespace(product.name).length > 0,
  );
  if (named.length === 0) return null;

  return (
    <div
      data-testid="product-identity"
      className={[
        "flex flex-wrap items-baseline gap-x-3 gap-y-1",
        "font-[family-name:var(--font-display)]",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {named.map((identity) => (
        <ProductVendorLabel
          key={`${identity.name}:${identity.vendor ?? ""}`}
          identity={identity}
        />
      ))}
    </div>
  );
}
